import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { addCrosstalkcolumnsToSelectedColumns } from '@ddv/crosstalk';
import { UserEntitlements, UserEntitlementService } from '@ddv/entitlements';
import { QueryParamsService } from '@ddv/filters';
import { ApiServices, ApiExecutorService } from '@ddv/http';
import { ManagerService } from '@ddv/layout';
import {
    Action,
    ActionState,
    crosstalkFieldMetadatas,
    DashboardPreference,
    DatasetFetchRuntimeParams,
    DatasetMetadata,
    FieldMetadata,
    isCellLevelEditActionHandler,
    MetadataLookup,
    moTradeReleaseCommentFieldId,
    NamedQuery,
    Query,
    QueryTypeName,
    WidgetFilterParams,
    WidgetIdNamedQueryId,
    MANAGE_WIDGET_WS_ID,
    NAMED_QUERY_NOT_FOUND,
    WIDGET_LIFECYCLE_EVENT,
} from '@ddv/models';
import { isAdditionalFilterNamesMatch, NamedQueriesService } from '@ddv/named-queries';
import { ClientsService } from '@ddv/reference-data';
import { deepCompare, deepClone } from '@ddv/utils';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, skipWhile, switchMap, take } from 'rxjs/operators';

import { DatasetRuntimeParamsBuilderService } from './dataset-runtime-params-builder.service';

interface MetadataQueryResponse {
    actions: Action[];
    metadata: FieldMetadata[];
    supportsDateRange: boolean;
    supportsGranularity: boolean;
}

export const replaceHostInActionsURLToken: InjectionToken<boolean> = new InjectionToken('replaceHostInActionsURLToken');
export const trebekApiURLToken: InjectionToken<string> = new InjectionToken('trebekApiURLToken');

@Injectable()
export class MetadataService {
    private readonly trebekApiExecutor = inject<ApiExecutorService>(ApiServices.trebek);
    private readonly cfbApiExecutor = inject<ApiExecutorService>(ApiServices.cfb);
    private readonly replaceHostInURL = inject(replaceHostInActionsURLToken);
    private readonly trebekApiURL = inject(trebekApiURLToken);
    private readonly currentStateService = inject(CurrentStateService);
    private readonly managerService = inject(ManagerService);
    private readonly queryParamsService = inject(QueryParamsService);
    private readonly clientsService = inject(ClientsService);
    private readonly namedQueriesService = inject(NamedQueriesService);
    private readonly runtimeParamsBuilderService = inject(DatasetRuntimeParamsBuilderService);
    private readonly userEntitlementsService = inject(UserEntitlementService);

    readonly metadataState$: Observable<DatasetMetadata>;
    readonly actionsState$: Observable<ActionState>;

    private userEntitlements: UserEntitlements | undefined;
    private readonly metadataStateSubject = new BehaviorSubject<DatasetMetadata>({ metadata: new Map() });
    private isMultiClient = false;
    private dashboardClientsList: string[] = [];
    private manageWidgetClientsList: string[] = [];
    private fullClientsList = '';
    private dashboardQueryParams: DashboardPreference | undefined;
    private readonly metadataDetails: Map<number, MetadataDetails> = new Map();
    private readonly actionsStateSubject = new BehaviorSubject<ActionState>({});
    private readonly namedQueries: Map<number, NamedQuery> = new Map();

    constructor() {
        this.metadataState$ = this.metadataStateSubject.asObservable();
        this.actionsState$ = this.actionsStateSubject.asObservable();

        this.currentStateService.isMultiClient$.pipe(switchMap((isMultiClient) => {
            this.isMultiClient = isMultiClient;
            if (this.isMultiClient) {
                return combineLatest([
                    this.queryParamsService.dashboardQueryParams,
                    this.queryParamsService.widgetQueryParams,
                    this.clientsService.clients(),
                ]);
            } else {
                return combineLatest([of({}), of({}), of([])]);
            }
        })).subscribe({
            next: ([dashboardQueryParams, widgetQueryParams, list]) => {
                if (this.isMultiClient) {
                    if (dashboardQueryParams && Object.keys(dashboardQueryParams).length) {
                        this.dashboardClientsList = (dashboardQueryParams as DashboardPreference).clients?.map((cl) => cl.clientId) ?? [];
                    }
                    if (widgetQueryParams && Object.keys(widgetQueryParams).length) {
                        (widgetQueryParams as WidgetFilterParams).widgetFilters.forEach((widgetFilters, _) => {
                            if (widgetFilters.clients) {
                                this.manageWidgetClientsList = widgetFilters.clients.map((client) => client.clientId);
                            }
                        });
                    }
                    this.fullClientsList = (list as string[]).join(',');
                }
            },
        });

        this.queryParamsService.dashboardQueryParams.subscribe({
            next: (dashboardQueryParams) => this.dashboardQueryParams = dashboardQueryParams,
        });

        this.currentStateService.clientCode$.pipe(
            switchMap((clientCode) => this.userEntitlementsService.fetchUserEntitlements(clientCode)),
        ).subscribe({
            next: (entitlements) => this.userEntitlements = entitlements,
        });
    }

    // this is called only by ApplicationBaseWidget.fetchMetaData
    getMetadata(context: WidgetIdNamedQueryId): void {
        this.fetchUniqueMetadata(context).subscribe({
            next: (metadata: MetadataLookup | undefined) => {
                if (metadata) {
                    Object.entries(metadata).forEach(([key, value]) => {
                        value.name = key;
                    });

                    if (context.widgetId) {
                        this.addMetadataToState(context.widgetId, metadata);
                    }
                }
            },
            error: (error: { message?: string }) => {
                if (error.message?.includes('All queries must use the same runtime parameters')) {
                    this.managerService.sendMessageToExistingWidget(context.widgetId ?? 0, {
                        action: WIDGET_LIFECYCLE_EVENT.ERROR_OCCURRED,
                        exception: { message: error.message, time: new Date().toISOString() },
                    });
                    this.managerService.sendMessageToAllWidgetsOnWorkspace(context.widgetId ?? 0, {
                        action: WIDGET_LIFECYCLE_EVENT.ERROR_OCCURRED,
                        exception: { message: error.message, time: new Date().toISOString() },
                    });
                }
            },
        });
    }

    addMetadataToState(widgetId: number, metadata: MetadataLookup | undefined): void {
        const currentState = this.metadataStateSubject.value;
        if (!deepCompare(currentState.metadata.get(widgetId), metadata)) {
            const newMetadata = deepClone(currentState.metadata);
            newMetadata.set(widgetId, deepClone(metadata ?? {}));

            const newState: DatasetMetadata = { ...currentState, lastChangedWidgetId: widgetId, metadata: newMetadata };
            this.metadataStateSubject.next(newState);
        }
    }

    removeMetadata(widgetId: number): void {
        const currentState = this.metadataStateSubject.value;
        const newMetadata: Map<number, MetadataLookup> = new Map();
        currentState.metadata.forEach((value, key) => {
            const keyNumber = Number(key);
            if (keyNumber !== widgetId) {
                if (value) {
                    newMetadata.set(keyNumber, value);
                }
            }
        });

        if (this.metadataDetails.get(widgetId)) {
            this.metadataDetails.delete(widgetId);
        }

        const newState: DatasetMetadata = { ...currentState, lastChangedWidgetId: undefined, metadata: newMetadata };
        this.metadataStateSubject.next(newState);

        this.namedQueries.delete(widgetId);
    }

    fetchUniqueMetadata(context: WidgetIdNamedQueryId): Observable<MetadataLookup | undefined> {
        const widgetId = context.widgetId;
        if (!widgetId) {
            return throwError(() => new Error('Cannot fetch metadata without a widget id'));
        }

        return this.fetchAndMapMetadata(context)
            .pipe(
                map((metadata: MetadataLookup | undefined) => {
                    const details = this.metadataDetails.get(widgetId);
                    if (details) {
                        details.metadata = of(metadata);
                    }

                    return metadata;
                }),
            );
    }

    fetchAllMetadata(contexts: WidgetIdNamedQueryId[]): Observable<Record<number, MetadataLookup | undefined>> {
        const fetchObservables = contexts.map((context) => {
            return this.fetchAndMapMetadata(context);
        });

        return forkJoin(fetchObservables)
            .pipe(map((metadatas: (MetadataLookup | undefined)[]) => {
                return metadatas.reduce((hash, metadata, index): Record<number, MetadataLookup> => {
                    const widgetId = contexts[index].widgetId;
                    if (!widgetId) {
                        return hash;
                    }

                    const details = this.metadataDetails.get(widgetId);
                    if (details) {
                        details.metadata = of(metadata);
                    }
                    if (metadata) {
                        hash[widgetId] = metadata;
                    }
                    return hash;
                }, {} as Record<number, MetadataLookup>);
            }));
    }

    private fetchAndMapMetadata(context: WidgetIdNamedQueryId): Observable<MetadataLookup | undefined> {
        if (!context.namedQueryId) {
            this.managerService.sendMessageToExistingWidget(context.widgetId ?? 0, {
                action: WIDGET_LIFECYCLE_EVENT.ERROR_OCCURRED,
                exception: { message: NAMED_QUERY_NOT_FOUND, time: new Date().toISOString() },
            });

            return of(undefined);
        }

        return this.namedQueriesService.fetchNamedQuery(context.namedQueryId)
            .pipe(mergeMap((namedQuery: NamedQuery | undefined) => {
                if (!namedQuery) {
                    return of(undefined);
                }

                this.namedQueries.set(context.widgetId ?? 0, namedQuery);
                if (!isAdditionalFilterNamesMatch([...this.namedQueries.values()])) {
                    return throwError(() => new Error('All queries must use the same runtime parameters'));
                }

                return this.getMetadataDetails(namedQuery, context);
            }));
    }

    private getMetadataDetails(
        namedQuery: NamedQuery,
        context: WidgetIdNamedQueryId,
    ): Observable<MetadataLookup | undefined> {
        return this.getQueryParams(namedQuery, context)
            .pipe(
                switchMap((params): Observable<Query> => {
                    if (namedQuery.normalizeQueryIsStacked()) {
                        return of(namedQuery.populateStackedQueryTemplate(params)[0]);
                    }
                    return this.namedQueriesService.fetchExecutableQuery(namedQuery, params);
                }),
                switchMap((query): Observable<MetadataLookup | undefined> => {
                    const conversableType = namedQuery.normalizeConversableType();
                    if (conversableType) {
                        query.conversationType = conversableType;
                        if (query.selectedColumns) {
                            addCrosstalkcolumnsToSelectedColumns(query.selectedColumns);
                        }
                    }

                    const dashboardId = this.managerService.getCurrentDashboardId();
                    const widgetId = context.widgetId;
                    if (!dashboardId || !widgetId) {
                        // Cannot get metadata details without a dashboardId AND a widgetId
                        return of(undefined);
                    }

                    if (this.shouldFetchMetadata(namedQuery, dashboardId, widgetId)) {
                        const clientCodeAndRoute = this.getClientCodeAndRoute(namedQuery);
                        if (this.isMultiClient) {
                            clientCodeAndRoute.route += `?clients=${dashboardId === MANAGE_WIDGET_WS_ID ?
                                (this.manageWidgetClientsList.length ? this.manageWidgetClientsList.join(',') : this.fullClientsList) :
                                (this.dashboardClientsList.length ? this.dashboardClientsList.join(',') : this.fullClientsList)}`;
                        }

                        const metadataRequest$: Observable<MetadataLookup> = this.fetchMetadata(
                            widgetId,
                            namedQuery,
                            clientCodeAndRoute,
                            query);

                        const metadataDetails = {
                            namedQuery,
                            metadata: metadataRequest$,
                            clients: this.isMultiClient ?
                                dashboardId === MANAGE_WIDGET_WS_ID ?
                                    this.manageWidgetClientsList :
                                    this.dashboardClientsList :
                                [],
                        };
                        this.metadataDetails.set(widgetId, metadataDetails);
                    }

                    const lookup = this.metadataDetails.get(widgetId)?.metadata;
                    if (!lookup) {
                        return throwError(() => new Error(`metadataDetails map does not have ${widgetId}`));
                    }
                    return lookup;
                }));
    }

    private fetchMetadata(
        widgetId: number,
        namedQuery: NamedQuery,
        clientCodeAndRoute: { clientCode: string, route: string },
        query: Query,
    ): Observable<MetadataLookup> {
        const metadata$: Observable<MetadataQueryResponse> = this.getMetadataObservable(
            namedQuery,
            clientCodeAndRoute,
            query);

        return metadata$.pipe(
            map((response: MetadataQueryResponse): MetadataLookup => {
                this.updateActionsState(namedQuery.id, response.actions);

                this.mapUdfNumberToDecimal(response.metadata);

                if (namedQuery?.normalizeConversableType()) {
                    response.metadata.push(...crosstalkFieldMetadatas);
                }
                return response.metadata.reduce((hash: MetadataLookup, row: FieldMetadata) => {
                    if (row.name) {
                        hash[row.name] = row;
                    }
                    return hash;
                }, {});
            }),
            catchError((error: HttpErrorResponse) => {
                const response = error;
                console.error(response);
                const message = {
                    action: WIDGET_LIFECYCLE_EVENT.ERROR_OCCURRED,
                    exception: { message: (response.error as string || response.message) },
                };

                this.managerService.sendMessageToExistingWidget(widgetId, message);
                if (response.url?.indexOf('tfl') !== -1 && response.status === 403) {
                    Object.assign(error, { message: 'You are not entitled to this action' });
                }
                return throwError(() => error);
            }),
        );
    }

    private getMetadataObservable(
        namedQuery: NamedQuery | undefined,
        clientCodeAndRoute: { clientCode: string, route: string },
        query: Query,
    ): Observable<MetadataQueryResponse> {
        const method = 'POST';
        if (requestMetadataFromTrebek(namedQuery?.normalizeQueryTypeName() ?? '')) {
            return this.trebekApiExecutor.invokeServiceWithBody<MetadataQueryResponse>(
                clientCodeAndRoute.clientCode,
                clientCodeAndRoute.route,
                method,
                query);
        }

        return this.cfbApiExecutor.invokeServiceWithBody<MetadataQueryResponse>(
            clientCodeAndRoute.clientCode,
            clientCodeAndRoute.route,
            method,
            query,
        ).pipe(
            map((metadata) => {
                this.removeTradeReleaseCommentMetadata(metadata, namedQuery?.type?.name);

                const mappedActions = this.mapActionsURLs(metadata.actions);
                return { ...metadata, actions: mappedActions };
            }),
        );
    }

    private removeTradeReleaseCommentMetadata(metadata: MetadataQueryResponse, queryType: string | undefined): void {
        if (queryType !== QueryTypeName.SETTLEMENT_BREAKS) {
            return;
        }

        if (this.userEntitlements?.getCostCenter() !== 'Middle Office') {
            metadata.metadata = metadata.metadata.filter((m) => m.name !== moTradeReleaseCommentFieldId);
        }
    }

    private mapActionsURLs(actions: Action[]): Action[] {
        if (!this.replaceHostInURL) {
            return actions;
        }

        return actions.map((action: Action) => {
            const url = this.getActionURL(action.handler.url);

            if (isCellLevelEditActionHandler(action.handler) && action.handler.picklistRoute) {
                const picklistRoute = this.getActionURL(action.handler.picklistRoute);
                return { ...action, handler: { ...action.handler, url, picklistRoute } };
            }

            return { ...action, handler: { ...action.handler, url } };
        });
    }

    private getActionURL(url: string): string {
        const pathname = new URL(url).pathname;
        const actionPathname = pathname.substring(pathname.indexOf('reverse-proxy'), pathname.length);
        return `${this.trebekApiURL}/${actionPathname}`;
    }

    private getQueryParams(
        namedQuery: NamedQuery,
        context: WidgetIdNamedQueryId,
    ): Observable<DatasetFetchRuntimeParams> {
        return combineLatest([this.queryParamsService.widgetQueryParams, this.managerService.activeWidgets])
            .pipe(skipWhile(([, widgets]) => !widgets.some((w) => w.id === context.widgetId)))
            .pipe(skipWhile(([widgetQueryParams, widgets]) => {
                return !widgets.find((w) => w.id === context.widgetId)?.getExtraPreferences()?.isSubscribedToDashboardFilters &&
                    (!!context.widgetId && !widgetQueryParams?.widgetFilters?.has(context.widgetId));
            }))
            .pipe(take(1))
            .pipe(map(([widgetQueryParams, widgets]) => {
                const widget = widgets.find((w) => w.id === context.widgetId)?.getExtraPreferences();
                const widgetQueryParam = widget?.id ? widgetQueryParams?.widgetFilters.get(widget?.id) : undefined;
                const preference = widget?.isSubscribedToDashboardFilters ? this.dashboardQueryParams : widgetQueryParam;

                return this.runtimeParamsBuilderService.build(
                    namedQuery.normalizeQueryTypeName(),
                    preference,
                    this.isMultiClient);
            }));
    }

    private shouldFetchMetadata(
        namedQuery: NamedQuery,
        dashboardId: string,
        widgetId: number,
    ): boolean {
        const metadataDetails = this.metadataDetails.get(widgetId);
        if (!metadataDetails) {
            return true;
        }

        const { type } = metadataDetails.namedQuery;
        if (!deepCompare(type, namedQuery.type)) {
            return true;
        }

        if (!this.isMultiClient) {
            return false;
        }

        const { clients } = metadataDetails;
        if (dashboardId === MANAGE_WIDGET_WS_ID) {
            return !deepCompare(clients, this.manageWidgetClientsList);
        }

        return !deepCompare(clients, this.dashboardClientsList);
    }

    private getClientCodeAndRoute(namedQuery: NamedQuery): { clientCode: string, route: string } {
        const metadataEndpoint = namedQuery.type.metadataEndpoint;
        const url = new URL(metadataEndpoint);
        const endpointParts = url.pathname.split('/');
        const clientCode = endpointParts[3];
        const route = endpointParts.slice(4).join('/');
        return { clientCode, route };
    }

    private updateActionsState(namedQueryId: string, actions: Action[]): void {
        const currentActionState = this.actionsStateSubject.value;

        if (
            currentActionState[namedQueryId] &&
            currentActionState[namedQueryId].length === actions.length &&
            currentActionState[namedQueryId].every((as) => actions.some((a) => a.name === as.name))
        ) {
            return;
        }

        const updatedActionState = { ...currentActionState, [namedQueryId]: actions };
        this.actionsStateSubject.next(updatedActionState);
    }

    private mapUdfNumberToDecimal(metadata: FieldMetadata[]): void {
        metadata.forEach((field) => {
            if (field.name?.toLowerCase().startsWith('udf_') && field.datatype === 'number') {
                field.datatype = 'decimal';
            }
        });
    }
}

function requestMetadataFromTrebek(queryTypeName: string): boolean {
    return queryTypeName === QueryTypeName.AD_HOC ||
        queryTypeName === QueryTypeName.INVESTORS_PERFORMANCE ||
        queryTypeName === QueryTypeName.INVESTORS_FUNDS_ASSETS ||
        queryTypeName === QueryTypeName.EOD ||
        queryTypeName === QueryTypeName.TRADE_FILE_SUMMARY ||
        queryTypeName === QueryTypeName.TRADE_FILE_DETAILS ||
        queryTypeName === QueryTypeName.TFL_INCOMPLETE_FILES ||
        queryTypeName === QueryTypeName.CHECKLIST;
}

interface MetadataDetails {
    namedQuery: NamedQuery;
    metadata: Observable<MetadataLookup | undefined>;
    clients: string[];
}
